import React from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useUnits } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { GradeBreakdown, GradeKey } from '@Types/riskResultsSummary.types';
import {
	buildYearSeries,
	formatResultsLength,
	gradeLabel,
	isWaterNetwork,
	orderSlices,
} from './resultsSummary.utils';
import { buildPieOptions, buildYearOptions } from './breakdownChartOptions';

interface Props {
	grade: GradeKey;
	breakdown: GradeBreakdown;
	assetType?: string;
	onClose: () => void;
}

// Venus-style "Asset Breakdown" (Material / Diameter semicircle pies + year
// line), sized by asset count; tooltips add length in the user's units.
export const KpiBreakdownPanel = ({
	grade,
	breakdown,
	assetType,
	onClose,
}: Props) => {
	const { t } = useGlobalization();
	const { system, getSystemValueFormatted } = useUnits();

	const formatLength = (metres: number) =>
		formatResultsLength(metres, system, getSystemValueFormatted);
	const unknownLabel = t('Unknown');
	const sliceTooltip = (label: string, count: number, metres: number) =>
		`${label}: <b>${t('{{count}} assets', { count })}</b> (${formatLength(
			metres,
		)})`;

	const material = orderSlices(breakdown.material, 'byCount');
	const diameter = orderSlices(breakdown.diameter, 'numericLabel');
	const years = buildYearSeries(breakdown.year_laid);
	// Every asset lands in exactly one material key (incl. "Unknown"), so no
	// material and no years means the grade has no assets.
	const isEmpty =
		material.length === 0 &&
		years.points.length === 0 &&
		years.unknownCount === 0;

	return (
		<Box
			sx={{ backgroundColor: 'white', p: 2 }}
			data-cy="risk-breakdown-panel">
			<Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="h6">
						{`${t('Asset Breakdown')}: ${gradeLabel(t, grade)}`}
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						{t('Click a card above to view a different grade')}
					</Typography>
				</Box>
				<IconButton
					onClick={onClose}
					size="small"
					sx={{ color: 'text.secondary' }}
					title={t('Hide breakdown')}
					data-cy="risk-breakdown-close">
					<CloseIcon />
				</IconButton>
			</Box>
			{breakdown.assets_with_empty_length > 0 && (
				<Box
					data-testid="risk-breakdown-empty-length"
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						mb: 2,
						p: 1,
						border: '1px solid #EDA641',
						backgroundColor: '#FBEDD4',
						color: '#746E67',
					}}>
					<WarningAmberIcon sx={{ color: '#EDA641' }} />
					<Typography variant="body2" sx={{ fontWeight: 600 }}>
						{t(
							'{{count}} assets with empty length were excluded from length totals.',
							{ count: breakdown.assets_with_empty_length },
						)}
					</Typography>
				</Box>
			)}
			{isEmpty ? (
				<Typography variant="body2">
					{t('No assets in this grade')}
				</Typography>
			) : (
				<Grid container spacing={2}>
					<Grid item xs={4}>
						<HighchartsReact
							highcharts={Highcharts}
							options={buildPieOptions({
								title: t('Material'),
								slices: material,
								formatLabel: (label, unknown) =>
									unknown ? unknownLabel : label,
								tooltipLine: sliceTooltip,
							})}
						/>
					</Grid>
					<Grid item xs={4}>
						<HighchartsReact
							highcharts={Highcharts}
							options={buildPieOptions({
								title: t('Diameter'),
								slices: diameter,
								formatLabel: (label, unknown) =>
									unknown
										? unknownLabel
										: getSystemValueFormatted(
												`${Number.parseFloat(label)}mm`,
										  ),
								tooltipLine: sliceTooltip,
							})}
						/>
					</Grid>
					<Grid item xs={4}>
						<HighchartsReact
							highcharts={Highcharts}
							options={buildYearOptions({
								title: isWaterNetwork(assetType)
									? t('Installation Year')
									: t('Year Laid'),
								points: years.points,
								tooltipLine: (year, count) =>
									`${year}: <b>${t('{{count}} assets', {
										count,
									})}</b>`,
							})}
						/>
						{years.unknownCount > 0 && (
							<Typography variant="caption">
								{t('{{count}} assets with unknown year', {
									count: years.unknownCount,
								})}
							</Typography>
						)}
					</Grid>
				</Grid>
			)}
		</Box>
	);
};

export default KpiBreakdownPanel;
